import type { ChapterItem } from '@app/_proto/Protos/chapters';
import type { NovelItem } from '@app/_proto/Protos/novels';
import { logSelectChapter, logSelectSeries } from '@app/analytics';
import { CHAPTER_EVENT_SOURCES } from '@app/analytics/constants';
import { ReactComponent as ChevronLeftSmall } from '@app/assets/chevron-left-small.svg';
import { ReactComponent as ChevronLeft } from '@app/assets/chevron-left.svg';
import { ReactComponent as ChevronRightSmall } from '@app/assets/chevron-right-small.svg';
import { ChapterEvents } from '@app/domains/chapter/analytics/posthog/events';
import { chapterAnalyticsFactory } from '@app/domains/chapter/analytics/posthog/handlers';
import ChapterTopNavigationButton from '@app/domains/chapter/components/ChapterTopNavigationButton';
import useChapterNavigationStatus from '@app/domains/chapter/hooks/useChapterNavigationStatus';
import BlurImg from '@app/domains/common/components/BlurImg';
import useChapterStatus from '@app/domains/common/hooks/useChapterStatus';
import useNovelStatus from '@app/domains/common/hooks/useNovelStatus';
import { getChapterPath, getNovelPath } from '@app/domains/common/utils/path';
import { Hidden, Typography } from '@mui/material';
import { useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';

type Props = {
    novel: NovelItem;
    chapter?: ChapterItem;
};

export default function ChapterTopNavigation({ novel, chapter }: Props) {
    const {
        name: novelName,
        slug: novelSlug,
        coverUrl: novelCoverUrl,
        coverBlurHash: novelCoverBlurHash,
    } = useNovelStatus({ novel });
    const { prevChapterSlug, nextChapterSlug } = useChapterStatus({ chapter });
    const { prevDisabled, nextDisabled } = useChapterNavigationStatus({ chapter });

    const navigate = useNavigate();

    const onClickPrev = () => {
        chapterAnalyticsFactory(ChapterEvents.ClickPreviousChapter)({
            novel,
            chapter,
            on: CHAPTER_EVENT_SOURCES.ChapterViewerArrow,
        });
        navigate(getChapterPath({ novelSlug, chapterSlug: prevChapterSlug }), {
            state: { from: CHAPTER_EVENT_SOURCES.ChapterViewerTopNavigation },
        });

        logSelectChapter(novel, chapter?.relatedChapterInfo?.previousChapter);
    };

    const onClickNext = () => {
        chapterAnalyticsFactory(ChapterEvents.ClickNextChapter)({
            novel,
            chapter,
            on: CHAPTER_EVENT_SOURCES.ChapterViewerArrow,
        });
        navigate(getChapterPath({ novelSlug, chapterSlug: nextChapterSlug }), {
            state: { from: CHAPTER_EVENT_SOURCES.ChapterViewerTopNavigation },
        });

        logSelectChapter(novel, chapter?.relatedChapterInfo?.nextChapter);
    };

    const onClickNovel = useCallback(() => {
        logSelectSeries(novel);

        navigate(getNovelPath(novelSlug as string));
    }, [novel, novelSlug, navigate]);

    return (
        <div className="flex items-center">
            <Hidden smDown implementation="css">
                <Link onClick={() => logSelectSeries(novel)} to={getNovelPath(novelSlug as string)}>
                    <ChevronLeft className="scale-150 transform text-[#b4b4b4]" />
                </Link>
            </Hidden>
            <div className="flex flex-1 items-center space-x-[10px] sm:ml-10">
                <BlurImg
                    className="cursor-pointer"
                    onClick={onClickNovel}
                    loading="eager"
                    priority
                    alt={novelName}
                    width={29}
                    height={42}
                    src={novelCoverUrl}
                    blurHash={novelCoverBlurHash}
                />
                <Link onClick={() => logSelectSeries(novel)} to={getNovelPath(novelSlug as string)}>
                    <Typography className="text-[13px] text-gray-t0 sm:text-[15px]" fontWeight={600}>
                        {novelName}
                    </Typography>
                </Link>
                <div className="flex-1">
                    <div className="flex justify-end space-x-[10px]">
                        <ChapterTopNavigationButton
                            data-testid="prev-chapter-button"
                            onClick={onClickPrev}
                            variant="outlined"
                            disabled={prevDisabled}
                        >
                            <ChevronLeftSmall className="text-gray-t0" />
                        </ChapterTopNavigationButton>
                        <ChapterTopNavigationButton
                            data-testid="next-chapter-button"
                            onClick={onClickNext}
                            variant="outlined"
                            disabled={nextDisabled}
                        >
                            <ChevronRightSmall className="text-gray-t0" />
                        </ChapterTopNavigationButton>
                    </div>
                </div>
            </div>
        </div>
    );
}
